import { useEffect, useState } from 'react';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

type SubmissionStatus = 'idle' | 'submitting' | 'success' | 'error';

interface SubmissionStep {
  label: string;
  duration: number; // ms to stay on this step
}

interface SubmissionProgressProps {
  status: SubmissionStatus;
  errorMessage?: string;
  onDismissError?: () => void;
}

const steps: SubmissionStep[] = [
  { label: 'Validating your request...', duration: 600 },
  { label: 'Uploading data...', duration: 1200 },
  { label: 'Creating your task...', duration: 2000 },
  { label: 'Finalizing...', duration: 1500 },
];

export function SubmissionProgress({ status, errorMessage, onDismissError }: SubmissionProgressProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (status !== 'submitting') {
      if (status === 'success') {
        setProgress(100);
        setCurrentStep(steps.length);
      }
      return;
    }

    setCurrentStep(0);
    setProgress(0);

    let stepIndex = 0;
    let elapsed = 0;
    const totalDuration = steps.reduce((sum, s) => sum + s.duration, 0);

    const interval = setInterval(() => {
      elapsed += 50;

      // Calculate cumulative progress
      let cumulativeDuration = 0;
      for (let i = 0; i < steps.length; i++) {
        if (elapsed < cumulativeDuration + steps[i].duration) {
          stepIndex = i;
          const stepProgress = (elapsed - cumulativeDuration) / steps[i].duration;
          const baseProgress = (cumulativeDuration / totalDuration) * 90;
          const stepContribution = (steps[i].duration / totalDuration) * 90 * stepProgress;
          setProgress(Math.min(baseProgress + stepContribution, 90));
          break;
        }
        cumulativeDuration += steps[i].duration;
      }

      setCurrentStep(stepIndex);

      // Cap at 90% — the last 10% happens when we get the actual response
      if (elapsed >= totalDuration) {
        setProgress(90);
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [status]);

  if (status === 'idle') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-bounce-in">
        {/* Status Icon */}
        <div className="flex justify-center mb-6">
          {status === 'submitting' && (
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center animate-bounce-in">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          )}
          {status === 'error' && (
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center animate-bounce-in">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          )}
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
          {status === 'submitting' && 'Submitting Your Request'}
          {status === 'success' && 'Request Submitted!'}
          {status === 'error' && 'Submission Failed'}
        </h3>

        {/* Step Label */}
        <p className="text-sm text-center text-gray-500 mb-6 min-h-[1.25rem]">
          {status === 'submitting' && currentStep < steps.length && steps[currentStep].label}
          {status === 'success' && 'Redirecting you shortly...'}
          {status === 'error' && (errorMessage || 'Something went wrong. Please try again.')}
        </p>

        {/* Progress Bar */}
        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ease-out ${
              status === 'error'
                ? 'bg-red-500'
                : status === 'success'
                  ? 'bg-green-500'
                  : 'bg-blue-600'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Progress Percentage */}
        <p className="text-xs text-center text-muted mt-2">
          {Math.round(progress)}%
        </p>

        {/* Error Dismiss Button */}
        {status === 'error' && onDismissError && (
          <button
            type="button"
            onClick={onDismissError}
            className="mt-4 w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
}
